import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { injectIntl, useIntl } from 'react-intl';
import { Formik, Form } from 'formik';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getDoc, createDoc, editDoc } from '../../../crud/docs.crud';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { getDocFromPathname } from './utils';
import { ButtonWithLoader } from '../../../components/ui/Buttons';
import { useDefineUserRole } from '../../../hooks';
import { IAppState } from '../../../store/rootDuck';
import CkEditorCustom from '../../../components/formComponents/CkEditorCustom';

const useStyles = makeStyles((theme: any) => ({
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  editor: {
    minHeight: 400,
  },
  cardEdit: {
    backgroundColor: '#ffff',
    width: 'calc((100% - 24px) / 2)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  formEdit: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    padding: theme.spacing(3),
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}));

interface IDocument {
  code: string;
  name: string;
  text: string;
  public: boolean;
  id?: number;
}

function UserDocPage({ me }: TPropsFromRedux) {
  const classes = useStyles();
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [userDocument, setDocument] = useState<IDocument | null>(null);
  const [editorState, setEditorState] = useState<any>(null);
  const { type } = useParams();
  const isRoleAdmin = useDefineUserRole(me, 'ROLE_ADMIN');
  const isRoleManager = useDefineUserRole(me, 'ROLE_MANAGER');

  const document = getDocFromPathname(type || '', intl);

  setLayoutSubheader({
    title: document.name,
    breadcrumb: [],
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    setLoading(true);
    getDoc(document.code)
      .then(({ data: requestData }) => {
        setDocument(requestData.data);
        setLoading(false);
      })
      .catch(() => {
        setDocument(null);
        setLoading(false);
      });
  }, [type, document.code]);

  const handleUnLoading = () => setLoading(false);

  const handleSubmit = async () => {
    const dataCurrent: IDocument = {
      code: document.code,
      name: document.name,
      text: editorState?.getData() || '',
      public: true,
    };
    setLoading(true);

    if (!userDocument) {
      createDoc(dataCurrent).then(handleUnLoading).catch(handleUnLoading);
    } else {
      editDoc(userDocument.id, dataCurrent).then(handleUnLoading).catch(handleUnLoading);
    }
  };

  return (
    <>
      <Formik initialValues={{}} onSubmit={handleSubmit}>
        {() => (
          <Form>
            <CkEditorCustom
              disabled={!!isRoleManager}
              html={userDocument?.text || ''}
              setEditorStateCallback={setEditorState}
            />
            <div className={classes.buttonContainer}>
              {isRoleAdmin && (
                <ButtonWithLoader onPress={handleSubmit} loading={loading}>
                  {userDocument
                    ? intl.formatMessage({ id: 'COMMON.BUTTON.SAVE' })
                    : intl.formatMessage({ id: 'COMMON.BUTTON.CREATE' })}
                </ButtonWithLoader>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

const connector = connect((state: IAppState) => ({
  me: state.profile.me,
}));
type TPropsFromRedux = ConnectedProps<typeof connector>;

export default injectIntl(connector(UserDocPage));
